import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Chart } from "@/components/Chart";
import { Zap, Droplet, Thermometer } from "lucide-react";
import { toast } from "sonner";

function TimeChart({ data, color }) {
    return (
        <Chart height={176}>
            <AreaChart data={data}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="ts" tickFormatter={(v) => v.slice(11, 16)} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={40} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={color} fillOpacity={0.15} />
            </AreaChart>
        </Chart>
    );
}

export default function Energy() {
    const [energy, setEnergy] = useState(null);
    const [water, setWater] = useState(null);
    const [temp, setTemp] = useState(null);
    const [rules, setRules] = useState(null);
    const [summary, setSummary] = useState(null);

    const load = async () => {
        const [a, b, c, r, s] = await Promise.all([
            api.get("/energy/readings", { params: { type: "energy_kwh" } }),
            api.get("/energy/readings", { params: { type: "water_l" } }),
            api.get("/energy/readings", { params: { type: "temp_c" } }),
            api.get("/energy/rules"),
            api.get("/energy/summary"),
        ]);
        setEnergy(a.data);
        setWater(b.data);
        setTemp(c.data);
        setRules(r.data);
        setSummary(s.data);
    };
    useEffect(() => {
        load();
    }, []);

    const toggle = async (r) => {
        await api.patch(`/energy/rules/${r.id}`, { enabled: !r.enabled });
        toast.success(`Rule ${!r.enabled ? "enabled" : "disabled"}`);
        load();
    };

    if (!energy || !rules || !summary) return <LoadingBlock rows={6} />;

    return (
        <>
            <PageHeader
                title="Energy / Water / IoT"
                subtitle="Live sensor feeds and automation rules. Edge-first, cloud-observed."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <Card>
                    <CardHeader className="pb-1"><CardTitle className="text-sm flex items-center gap-2"><Zap className="w-4 h-4"/> Energy (kWh, last 24h)</CardTitle></CardHeader>
                    <CardContent>
                        <div className="text-2xl font-semibold tabular mb-1">{summary.totals?.energy_kwh ?? 0}</div>
                        <TimeChart data={energy} color="hsl(var(--primary))" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-1"><CardTitle className="text-sm flex items-center gap-2"><Droplet className="w-4 h-4"/> Water (L)</CardTitle></CardHeader>
                    <CardContent>
                        <div className="text-2xl font-semibold tabular mb-1">{summary.totals?.water_l ?? 0}</div>
                        <TimeChart data={water} color="hsl(var(--info))" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-1"><CardTitle className="text-sm flex items-center gap-2"><Thermometer className="w-4 h-4"/> Lobby Temp (°C)</CardTitle></CardHeader>
                    <CardContent>
                        <div className="text-2xl font-semibold tabular mb-1">{summary.averages?.temp_c ?? 0}</div>
                        <TimeChart data={temp} color="hsl(var(--chart-2))" />
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader><CardTitle className="text-base">Automation rules</CardTitle></CardHeader>
                <CardContent className="divide-y divide-border" data-testid="energy-rules">
                    {rules.map((r) => (
                        <div key={r.id} className="py-3 flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-medium">{r.name}</div>
                                <div className="text-xs text-muted-foreground">When: {r.when} → Then: {r.then}</div>
                            </div>
                            <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid="energy-rule-toggle" />
                        </div>
                    ))}
                </CardContent>
            </Card>
        </>
    );
}
